// All metric calculations — pure functions, no side effects, testable.
import {
  fromISO,
  monthKey,
  monthKeyFromDate,
  startOfMonth,
  endOfMonth,
  addMonths,
  monthsBetween,
  monthRange,
  today,
} from "./dateUtils";

// ── Helpers ────────────────────────────────────────────────────────────────
// Status taxonomy: "active" | "free_pause" | "paid_pause" | "cancelled" | "completed"
// Legacy values "paused" and "churned" still understood for backward compat.
// Key distinction: "completed" = project naturally ended (NOT churn).
//                  "cancelled" = client left early — counts as churn.
const isRecurring = (c) => c.primaryType === "recurring";
const isActive = (c) => c.status === "active";
const isCancelled = (c) => c.status === "cancelled" || c.status === "churned";
const isCompleted = (c) => c.status === "completed";
// Both states use churnDate as the end-of-engagement date.
const hasEnded = (c) => isCancelled(c) || isCompleted(c);
const isPaidPause = (c) => c.status === "paid_pause";
// "Currently paying" = active OR paid_pause. Used by MRR.
const isPaying = (c) => isActive(c) || isPaidPause(c);
// Legacy alias for callers still using isChurned-like logic — kept narrow
// so churn-rate code doesn't accidentally count completed clients.
const isChurned = isCancelled;
const isPaid = (p) => p.status === "paid" && p.paidDate;
const isPending = (p) => p.status === "pending";
const isScheduled = (p) => p.status === "scheduled";

// ── Money metrics ──────────────────────────────────────────────────────────

export function calculateMRR(clients, _offers) {
  // Includes active AND paid_pause recurring clients (both still paying).
  // Each client carries its own monthlyRate — offer.defaultAmount is no
  // longer used as a fallback (pricing is per-client only).
  return clients
    .filter((c) => isRecurring(c) && isPaying(c))
    .reduce((sum, c) => sum + (Number(c.monthlyRate) || 0), 0);
}

export function calculateARR(clients, offers) {
  return calculateMRR(clients, offers) * 12;
}

export function calculateCashCollected(payments, startDate, endDate) {
  return payments
    .filter((p) => {
      if (!isPaid(p)) return false;
      const d = fromISO(p.paidDate);
      if (!d) return false;
      if (startDate && d < startDate) return false;
      if (endDate && d > endDate) return false;
      return true;
    })
    .reduce((sum, p) => sum + (p.amount || 0), 0);
}

export function calculateBookedRevenue(payments, startDate, endDate) {
  return payments
    .filter((p) => {
      if (p.status === "scheduled") return false; // scheduled = not yet booked
      const d = fromISO(p.bookedDate);
      if (!d) return false;
      if (startDate && d < startDate) return false;
      if (endDate && d > endDate) return false;
      return true;
    })
    .reduce((sum, p) => sum + (p.amount || 0), 0);
}

export function calculateOutstandingAR(payments) {
  return payments.filter(isPending).reduce((sum, p) => sum + (p.amount || 0), 0);
}

export function calculateExpectedRevenue(payments, startDate, endDate) {
  return payments
    .filter((p) => {
      if (!isScheduled(p)) return false;
      const d = fromISO(p.dueDate);
      if (!d) return false;
      if (startDate && d < startDate) return false;
      if (endDate && d > endDate) return false;
      return true;
    })
    .reduce((sum, p) => sum + (p.amount || 0), 0);
}

export function calculateProjectRevenueYTD(payments, offers) {
  const oneOffIds = new Set(offers.filter((o) => o.type === "oneoff").map((o) => o.id));
  const yearStart = new Date(today().getFullYear(), 0, 1);
  return payments
    .filter((p) => oneOffIds.has(p.offerId) && p.status !== "scheduled")
    .filter((p) => {
      const d = fromISO(p.bookedDate);
      return d && d >= yearStart;
    })
    .reduce((sum, p) => sum + (p.amount || 0), 0);
}

// ── Client lifecycle ───────────────────────────────────────────────────────

export function calculateMonthlyChurn(clients, monthDate) {
  const recurring = clients.filter(isRecurring);
  const monthStart = startOfMonth(monthDate);
  const monthEnd = endOfMonth(monthDate);

  const activeAtStart = recurring.filter((c) => {
    const start = fromISO(c.startDate);
    if (!start || start >= monthStart) return false;
    // Exclude clients whose engagement already ended before this month
    // (cancelled OR completed — both have a real end date).
    if (hasEnded(c) && c.churnDate) {
      const end = fromISO(c.churnDate);
      if (end && end < monthStart) return false;
    }
    return true;
  }).length;

  if (activeAtStart === 0) return 0;

  const churnedThisMonth = recurring.filter((c) => {
    if (!isCancelled(c)) return false;
    const churn = fromISO(c.churnDate);
    return churn && churn >= monthStart && churn <= monthEnd;
  }).length;

  return (churnedThisMonth / activeAtStart) * 100;
}

export function calculateAnnualChurn(monthlyChurnPercent) {
  const m = (monthlyChurnPercent || 0) / 100;
  return (1 - Math.pow(1 - m, 12)) * 100;
}

export function calculateNewClientsThisMonth(clients) {
  const monthStart = startOfMonth(today());
  return clients.filter((c) => {
    if (c.status === "buyer") return false; // buyers don't count as new clients
    const start = fromISO(c.startDate);
    return start && start >= monthStart;
  }).length;
}

export function calculateChurnedThisMonth(clients) {
  const monthStart = startOfMonth(today());
  return clients.filter((c) => {
    if (!isChurned(c)) return false;
    const churn = fromISO(c.churnDate);
    return churn && churn >= monthStart;
  }).length;
}

// ── LTV & lifetime ─────────────────────────────────────────────────────────

export function calculateClientLTV(clientId, payments) {
  return payments
    .filter((p) => p.clientId === clientId && isPaid(p))
    .reduce((sum, p) => sum + (p.amount || 0), 0);
}

export function calculateAverageLTV(clients, payments) {
  const recurring = clients.filter(isRecurring);
  if (recurring.length === 0) return 0;
  const total = recurring.reduce((sum, c) => sum + calculateClientLTV(c.id, payments), 0);
  return total / recurring.length;
}

export function calculateAverageLifetime(clients) {
  const churned = clients.filter((c) => isRecurring(c) && isChurned(c) && c.churnDate);
  if (churned.length === 0) return 0;
  const months = churned.map((c) => monthsBetween(c.startDate, c.churnDate));
  return months.reduce((a, b) => a + b, 0) / months.length;
}

export function calculateMedianLifetime(clients) {
  const churned = clients.filter((c) => isRecurring(c) && isChurned(c) && c.churnDate);
  if (churned.length === 0) return 0;
  const months = churned.map((c) => monthsBetween(c.startDate, c.churnDate)).sort((a, b) => a - b);
  const mid = Math.floor(months.length / 2);
  return months.length % 2 === 0 ? (months[mid - 1] + months[mid]) / 2 : months[mid];
}

export function calculateClientMonthsActive(client) {
  const start = fromISO(client.startDate);
  if (!start) return 0;
  const end = isCancelled(client) && client.churnDate ? fromISO(client.churnDate) : today();
  return Math.max(0, monthsBetween(client.startDate, end instanceof Date ? end.toISOString().slice(0, 10) : end));
}

// ── Payment matrix ─────────────────────────────────────────────────────────

export function getClientPaymentMatrix(clientId, payments, _offers, monthsAhead = 18) {
  const clientPayments = payments.filter((p) => p.clientId === clientId);
  if (clientPayments.length === 0) {
    return { months: [], revenueCells: [], collectedCells: [] };
  }

  const dates = clientPayments
    .map((p) => fromISO(p.bookedDate) || fromISO(p.dueDate))
    .filter(Boolean);
  const earliest = dates.reduce((min, d) => (d < min ? d : min), dates[0]);
  const months = monthRange(earliest, monthsAhead);

  // One Revenue row + one Cash collected row across ALL of this client's
  // payments. Per-offer grouping is intentionally dropped — the matrix is
  // a per-client view, not a per-offer one (use the Dashboard per-offer
  // sub-tab for that).
  const revenueCells = months.map((m) => {
    const mKey = monthKeyFromDate(m);
    const inMonth = clientPayments.filter((p) => {
      const ref = p.paidDate || p.dueDate || p.bookedDate;
      return monthKey(ref) === mKey;
    });
    const total = inMonth.reduce((s, p) => s + (p.amount || 0), 0);
    const status =
      inMonth.length === 0
        ? null
        : inMonth.every((p) => p.status === "paid")
        ? "paid"
        : inMonth.every((p) => p.status === "scheduled")
        ? "scheduled"
        : inMonth.some((p) => p.status === "pending")
        ? "pending"
        : "mixed";
    return { monthKey: mKey, amount: total, status, payments: inMonth };
  });

  const collectedCells = months.map((m) => {
    const mKey = monthKeyFromDate(m);
    const inMonth = clientPayments.filter((p) => {
      if (p.status !== "paid") return false;
      const ref = p.paidDate || p.dueDate || p.bookedDate;
      return monthKey(ref) === mKey;
    });
    const total = inMonth.reduce((s, p) => s + (p.amount || 0), 0);
    return { monthKey: mKey, amount: total, payments: inMonth };
  });

  return { months, revenueCells, collectedCells };
}

// ── Time series for charts ─────────────────────────────────────────────────

export function getCashVsExpectedSeries(payments, monthsBack = 6, monthsForward = 6) {
  const t = today();
  const series = [];
  for (let i = -monthsBack; i < monthsForward; i++) {
    const m = addMonths(t, i);
    const mStart = startOfMonth(m);
    const mEnd = endOfMonth(m);
    const cash = calculateCashCollected(payments, mStart, mEnd);
    const expected = calculateExpectedRevenue(payments, mStart, mEnd);
    series.push({
      month: m.toLocaleDateString("en-US", { month: "short", year: "2-digit" }),
      cash,
      expected,
    });
  }
  return series;
}

export function getNewVsChurnedSeries(clients, monthsBack = 12) {
  const t = today();
  const series = [];
  for (let i = -monthsBack + 1; i <= 0; i++) {
    const m = addMonths(t, i);
    const mStart = startOfMonth(m);
    const mEnd = endOfMonth(m);
    const recurring = clients.filter(isRecurring);
    const newCount = recurring.filter((c) => {
      const s = fromISO(c.startDate);
      return s && s >= mStart && s <= mEnd;
    }).length;
    const churnedCount = recurring.filter((c) => {
      if (!isChurned(c)) return false;
      const ch = fromISO(c.churnDate);
      return ch && ch >= mStart && ch <= mEnd;
    }).length;
    series.push({
      month: m.toLocaleDateString("en-US", { month: "short", year: "2-digit" }),
      new: newCount,
      churned: churnedCount,
    });
  }
  return series;
}

export function getChurnRateSeries(clients, monthsBack = 12) {
  const t = today();
  const series = [];
  for (let i = -monthsBack + 1; i <= 0; i++) {
    const m = addMonths(t, i);
    series.push({
      month: m.toLocaleDateString("en-US", { month: "short", year: "2-digit" }),
      churnRate: calculateMonthlyChurn(clients, m),
    });
  }
  return series;
}

// ── Cohort retention ───────────────────────────────────────────────────────

export function getCohortRetention(clients) {
  const recurring = clients.filter(isRecurring);
  if (recurring.length === 0) return { cohorts: [], maxMonths: 0 };

  const byCohort = {};
  recurring.forEach((c) => {
    const cohort = monthKey(c.startDate);
    if (!cohort) return;
    if (!byCohort[cohort]) byCohort[cohort] = [];
    byCohort[cohort].push(c);
  });

  const cohortKeys = Object.keys(byCohort).sort();
  const t = today();

  const cohorts = cohortKeys.map((cohortKey) => {
    const cohortClients = byCohort[cohortKey];
    const cohortDate = new Date(cohortKey + "-01");
    const monthsSince = monthsBetween(cohortDate, t);
    const retention = [];
    for (let mi = 0; mi <= monthsSince; mi++) {
      const checkDate = addMonths(cohortDate, mi);
      const stillActive = cohortClients.filter((c) => {
        if (c.status !== "churned") return true;
        const ch = fromISO(c.churnDate);
        return ch && ch > checkDate;
      }).length;
      retention.push({
        monthIndex: mi,
        active: stillActive,
        pct: (stillActive / cohortClients.length) * 100,
      });
    }
    return {
      cohortKey,
      cohortLabel: cohortDate.toLocaleDateString("en-US", { month: "short", year: "numeric" }),
      size: cohortClients.length,
      retention,
    };
  });

  const maxMonths = Math.max(0, ...cohorts.map((c) => c.retention.length));
  return { cohorts, maxMonths };
}

// ── Per-country / per-offer aggregates ─────────────────────────────────────

export function aggregateByCountry(clients, payments, offers) {
  const byCountry = {};
  clients.forEach((c) => {
    if (!c.country) return;
    if (!byCountry[c.country]) {
      byCountry[c.country] = {
        country: c.country,
        active: 0,
        total: 0,
        mrr: 0,
        cashCollected: 0,
        ltvSum: 0,
        recurringCount: 0,
      };
    }
    const bucket = byCountry[c.country];
    bucket.total += 1;
    if (isActive(c)) bucket.active += 1;
    if (isRecurring(c) && isActive(c)) {
      bucket.mrr += Number(c.monthlyRate) || 0;
    }
    const clientLTV = calculateClientLTV(c.id, payments);
    bucket.cashCollected += clientLTV;
    if (isRecurring(c)) {
      bucket.ltvSum += clientLTV;
      bucket.recurringCount += 1;
    }
  });
  return Object.values(byCountry).map((b) => ({
    ...b,
    avgLTV: b.recurringCount > 0 ? b.ltvSum / b.recurringCount : 0,
  }));
}

// Resolve a payment's effective offer: explicit payment.offerId if set,
// otherwise inherit from the linked client's primaryOfferId. Lets revenue
// roll up even when a payment record (often a GHL invoice) doesn't carry
// an offer tag of its own.
function paymentOfferId(p, clients) {
  if (p.offerId) return p.offerId;
  const c = clients.find((x) => x.id === p.clientId);
  return c?.primaryOfferId || null;
}

export function aggregateByOffer(clients, payments, offers) {
  return offers.map((offer) => {
    const offerClients = clients.filter((c) => c.primaryOfferId === offer.id);
    const totalRevenue = payments
      .filter((p) => isPaid(p) && paymentOfferId(p, clients) === offer.id)
      .reduce((sum, p) => sum + (p.amount || 0), 0);
    const ltvList = offerClients.map((c) => calculateClientLTV(c.id, payments));
    const avgLTV = ltvList.length > 0 ? ltvList.reduce((a, b) => a + b, 0) / ltvList.length : 0;
    const churned = offerClients.filter((c) => isCancelled(c) && c.churnDate);
    const avgLifetime =
      churned.length > 0
        ? churned.map((c) => monthsBetween(c.startDate, c.churnDate)).reduce((a, b) => a + b, 0) /
          churned.length
        : 0;
    return {
      ...offer,
      clientCount: offerClients.length,
      totalRevenue,
      avgLTV,
      avgLifetime,
    };
  });
}

export function aggregateBySource(clients, payments) {
  const recurring = clients.filter(isRecurring);
  const grouped = {};
  recurring.forEach((c) => {
    const src = c.acquisitionSource || "Other";
    if (!grouped[src]) grouped[src] = { source: src, count: 0, ltvSum: 0 };
    grouped[src].count += 1;
    grouped[src].ltvSum += calculateClientLTV(c.id, payments);
  });
  return Object.values(grouped).map((g) => ({
    source: g.source,
    count: g.count,
    avgLTV: g.count > 0 ? g.ltvSum / g.count : 0,
  }));
}

export function aggregateByType(clients, payments) {
  const recurring = clients.filter(isRecurring);
  const grouped = {};
  recurring.forEach((c) => {
    const t = c.type || "Other";
    if (!grouped[t]) grouped[t] = { type: t, count: 0, ltvSum: 0 };
    grouped[t].count += 1;
    grouped[t].ltvSum += calculateClientLTV(c.id, payments);
  });
  return Object.values(grouped).map((g) => ({
    type: g.type,
    count: g.count,
    avgLTV: g.count > 0 ? g.ltvSum / g.count : 0,
  }));
}

export function getTopClients(clients, payments, limit = 10) {
  return clients
    .map((c) => ({ ...c, ltv: calculateClientLTV(c.id, payments) }))
    .sort((a, b) => b.ltv - a.ltv)
    .slice(0, limit);
}

// ── Email-grouped aggregations ─────────────────────────────────────────────
// Same email = same real-world business. Multiple Client records with the
// same email represent multiple engagements (offers) for the same customer.

const normalizeEmail = (e) => (e || "").trim().toLowerCase();

export function getClientsByEmail(email, clients) {
  const target = normalizeEmail(email);
  if (!target) return [];
  return clients.filter((c) => normalizeEmail(c.email) === target);
}

// Sum payments across all Client records sharing this email.
export function calculateCombinedLTVByEmail(email, clients, payments) {
  const matching = getClientsByEmail(email, clients);
  if (matching.length === 0) return 0;
  const ids = new Set(matching.map((c) => c.id));
  return payments
    .filter((p) => ids.has(p.clientId) && p.status === "paid" && p.paidDate)
    .reduce((sum, p) => sum + (p.amount || 0), 0);
}

// Top clients grouped by email — collapses multi-engagement clients to one row.
export function getTopClientsByEmail(clients, payments, limit = 10) {
  const groups = new Map();
  clients.forEach((c) => {
    const email = normalizeEmail(c.email);
    const key = email || `__no_email__${c.id}`;
    const ltv = calculateClientLTV(c.id, payments);
    if (!groups.has(key)) {
      groups.set(key, {
        email: email || null,
        name: c.name,
        country: c.country,
        status: c.status,
        startDate: c.startDate,
        engagements: 1,
        ltv,
      });
    } else {
      const g = groups.get(key);
      g.engagements += 1;
      g.ltv += ltv;
      // Earliest startDate wins as the "first engagement"
      if (c.startDate && c.startDate < g.startDate) g.startDate = c.startDate;
      // If any are active, the group is active
      if (c.status === "active") g.status = "active";
    }
  });
  return [...groups.values()].sort((a, b) => b.ltv - a.ltv).slice(0, limit);
}

// ── Per-offer P&L ──────────────────────────────────────────────────────────
// Was this team member active during the given month key "YYYY-MM"?
function teamActiveInMonth(member, mKey) {
  if (!member.startDate) return false;
  const monthStart = `${mKey}-01`;
  const [y, m] = mKey.split("-").map(Number);
  const nextY = m === 12 ? y + 1 : y;
  const nextM = m === 12 ? 1 : m + 1;
  const monthEndExclusive = `${nextY}-${String(nextM).padStart(2, "0")}-01`;
  if (member.startDate >= monthEndExclusive) return false;
  if (member.endDate && member.endDate < monthStart) return false;
  return true;
}

// Count unpaid vacation days for a member that fall within month `mKey`.
// Vacations spanning months are split day-by-day across the months they
// overlap. Returns 0 when no vacations are passed in.
export function vacationDaysInMonth(member, mKey, vacations = []) {
  if (!member || !vacations.length) return 0;
  const [y, m] = mKey.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const monthStart = `${mKey}-01`;
  const monthEnd = `${mKey}-${String(daysInMonth).padStart(2, "0")}`;
  let total = 0;
  for (const v of vacations) {
    if (v.memberId !== member.id) continue;
    if (!v.startDate || !v.endDate) continue;
    const vStart = v.startDate > monthStart ? v.startDate : monthStart;
    const vEnd = v.endDate < monthEnd ? v.endDate : monthEnd;
    if (vEnd < vStart) continue;
    const startDay = Number(vStart.slice(8, 10));
    const endDay = Number(vEnd.slice(8, 10));
    total += endDay - startDay + 1;
  }
  // Cap at daysInMonth in case of overlapping vacation entries.
  return Math.min(total, daysInMonth);
}

// Fraction of the month a member was actually paid for (0..1). Prorates
// for partial start/end months so a Jan 15 starter on a 31-day month pays
// 17/31 of salary. Also subtracts unpaid vacation days that fall in the
// month. Returns 0 when the member wasn't active at all in mKey.
export function teamMonthFraction(member, mKey, vacations = []) {
  if (!member || !member.startDate) return 0;
  const [y, m] = mKey.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const monthFirstDay = 1;
  const monthLastDay = daysInMonth;

  const sParts = member.startDate.split("-").map(Number);
  const startsBeforeMonth = sParts[0] < y || (sParts[0] === y && sParts[1] < m);
  const startsAfterMonth = sParts[0] > y || (sParts[0] === y && sParts[1] > m);
  if (startsAfterMonth) return 0;
  const startDay = startsBeforeMonth ? monthFirstDay : sParts[2];

  let endDay = monthLastDay;
  if (member.endDate) {
    const eParts = member.endDate.split("-").map(Number);
    const endsBeforeMonth = eParts[0] < y || (eParts[0] === y && eParts[1] < m);
    const endsAfterMonth = eParts[0] > y || (eParts[0] === y && eParts[1] > m);
    if (endsBeforeMonth) return 0;
    if (!endsAfterMonth) endDay = eParts[2];
  }

  if (endDay < startDay) return 0;
  const employedDays = endDay - startDay + 1;
  const vacationDays = vacationDaysInMonth(member, mKey, vacations);
  const paidDays = Math.max(0, employedDays - vacationDays);
  return paidDays / daysInMonth;
}

// Pick the FX rate for a given month, falling back to the most recent
// prior month if no exact match. Mirrors TeamForm's pickRate logic so the
// matrix and the form agree.
function pickFxRateForMonth(fxRates, mKey) {
  if (!fxRates || fxRates.length === 0) return null;
  const sorted = [...fxRates].sort((a, b) => a.month.localeCompare(b.month));
  const exact = sorted.find((r) => r.month === mKey);
  if (exact) return Number(exact.usdToEgp) || null;
  const prior = [...sorted].reverse().find((r) => r.month <= mKey);
  if (prior) return Number(prior.usdToEgp) || null;
  // No prior rate — fall back to the earliest known rate so EGP salaries
  // still display rather than going blank.
  return Number(sorted[0].usdToEgp) || null;
}

// Resolve the base monthly salary in USD for a given month. If the member
// stored their salary in EGP (nativeCurrency === "EGP"), convert using the
// FX rate for that month (with nearest-prior fallback). Otherwise use the
// stored monthlySalary (already USD).
export function resolveBaseSalaryUSD(member, mKey, fxRates = []) {
  if (!member) return 0;
  if (member.nativeCurrency === "EGP" && Number(member.nativeSalary) > 0) {
    const rate = pickFxRateForMonth(fxRates, mKey);
    if (rate && rate > 0) return Number(member.nativeSalary) / rate;
  }
  return Number(member.monthlySalary) || 0;
}

// Resolve the salary actually paid to a member in a given month.
// Prefers salaryByMonth[mKey] (explicit override). Otherwise computes the
// month's base salary in USD (with FX conversion for EGP-native members)
// and prorates by employment fraction and vacation days.
export function resolveTeamMonthSalary(member, mKey, vacations = [], fxRates = []) {
  if (!member) return 0;
  const explicit = member.salaryByMonth?.[mKey];
  if (explicit != null && explicit !== "") return Number(explicit) || 0;
  const base = resolveBaseSalaryUSD(member, mKey, fxRates);
  return base * teamMonthFraction(member, mKey, vacations);
}

// Resolve the bonus paid to a member in a given month (default 0).
export function resolveTeamMonthBonus(member, mKey) {
  if (!member) return 0;
  const v = member.bonusByMonth?.[mKey];
  return v == null || v === "" ? 0 : Number(v) || 0;
}

// Effective monthly cost base for a tool = sum of bank-feed transactions
// matched to this tool in that month. Refunds (direction "in") subtract.
// Source of truth is the actual QuickBooks-synced charges, not a manual
// planned units × price entry (that pre-allocations data model is retired).
function resolveToolMonthBase(tool, mKey, oneOffs) {
  let actual = 0;
  for (const p of oneOffs || []) {
    if (p.matchedSourceType !== "tool" || p.matchedSourceId !== tool.id) continue;
    if (!p.date || monthKey(p.date) !== mKey) continue;
    if (p.status === "flagged") continue;
    const sign = (p.direction || "out") === "out" ? 1 : -1;
    actual += sign * (Number(p.amount) || 0);
  }
  return actual > 0 ? actual : 0;
}

// Compute the dollar cost attributed to a "view" from a list of allocations,
// partitioned by COGS vs OPEX.
//
// Each allocation row has shape:
//   { offerId?, scope?: "offer"|"department"|"agency", scopeId?: string,
//     mode: "percent"|"amount", value, costType: "cogs"|"opex" }
//
// Backwards compat: rows without `scope` default to "offer" + use `offerId`.
//
// view shapes:
//   { type: "offer", offerId }           — single-offer rollup
//   { type: "department", department }   — single-department rollup
//   { type: "agency" }                   — agency-total rollup
//
// Distribution rules:
//   - scope="offer"      → contributes only to that specific offer (and the
//                          department/agency that offer belongs to).
//   - scope="department" → contributes only to that department. For per-offer
//                          views, splits the cost equally across the offers
//                          inside that department.
//   - scope="agency"     → contributes only at agency-total. We deliberately
//                          do NOT pollute department margins with overhead;
//                          per-offer P&L also skips it. Reach it via Agency
//                          view only.
function costFromAllocations(allocations, view, base, offers = []) {
  const result = { cogs: 0, opex: 0, marketing: 0 };
  if (!allocations) return result;

  const baseAmt = Number(base) || 0;

  for (const a of allocations) {
    const scope = a.scope || (a.offerId ? "offer" : null);
    if (!scope) continue;
    const v = Number(a.value) || 0;
    const amt = a.mode === "amount" ? v : baseAmt * (v / 100);
    if (!(amt > 0)) continue;
    // Three buckets — Marketing is its own line on the P&L. COGS hits gross
    // margin; OPEX + Marketing hit operating margin equally but are reported
    // separately so ad spend is visible at a glance.
    const rawType = a.costType || "cogs";
    const type = rawType === "marketing" ? "marketing" : rawType === "opex" ? "opex" : "cogs";

    let contribution = 0;

    if (view.type === "agency") {
      // Every allocation contributes its full amount at the agency level.
      contribution = amt;
    } else if (view.type === "offer") {
      if (scope === "offer") {
        if (a.offerId === view.offerId) contribution = amt;
      } else if (scope === "department") {
        const targetOffer = offers.find((o) => o.id === view.offerId);
        if (targetOffer && targetOffer.department === a.scopeId) {
          const deptOffers = offers.filter((o) => o.department === a.scopeId);
          contribution = deptOffers.length > 0 ? amt / deptOffers.length : 0;
        }
      } else if (scope === "agency") {
        // Intentionally skip — agency overhead doesn't drag a single offer's P&L.
        contribution = 0;
      }
    } else if (view.type === "department") {
      if (scope === "offer") {
        const sourceOffer = offers.find((o) => o.id === a.offerId);
        if (sourceOffer && sourceOffer.department === view.department) contribution = amt;
      } else if (scope === "department") {
        if (a.scopeId === view.department) contribution = amt;
      } else if (scope === "agency") {
        // Agency overhead deliberately omitted from department margins.
        contribution = 0;
      }
    }

    if (contribution > 0) result[type] += contribution;
  }
  return result;
}

// Aggregate a single month's P&L for one offer. Pass offerId = null to
// aggregate across all offers (agency-wide view).
//
// Returns:
// {
//   revenue,
//   cogs:  { team, tools, oneOffs, total },
//   opex:  { team, tools, oneOffs, total },
//   grossProfit,  // revenue - cogs.total
//   netProfit,    // grossProfit - opex.total
//   contributingTeam:  { cogs: [...], opex: [...] },
//   contributingTools: { cogs: [...], opex: [...] },
//   oneOffsByType:     { cogs: [...], opex: [...] },
// }
export function aggregateOfferMonth(offerId, state, mKey) {
  const payments = state.payments || [];
  const clients = state.clients || [];
  const team = state.team || [];
  const tools = state.tools || [];
  const offers = state.offers || [];
  // View passed to costFromAllocations — null offerId == agency rollup.
  const view = offerId == null ? { type: "agency" } : { type: "offer", offerId };
  // state.oneOffPayments is intentionally not read here — bank-feed
  // transactions do not contribute to P&L cost in the current model.

  // Revenue: paid payments where the effective offer is this one (explicit
  // payment.offerId, or client.primaryOfferId if the payment isn't tagged).
  // When offerId is null (agency view), include all paid payments.
  // Uses amountRevenue (pass-through model): a $10k payment split into $3k
  // commission + $7k client-budget counts only the $3k as P&L revenue.
  // Legacy rows have amountRevenue = amount via the mapper fallback.
  const revenue = payments
    .filter((p) => {
      if (p.status !== "paid") return false;
      if (offerId == null) return true;
      return paymentOfferId(p, clients) === offerId;
    })
    .filter((p) => {
      const ref = p.paidDate || p.bookedDate;
      return ref && monthKey(ref) === mKey;
    })
    .reduce((s, p) => s + (Number(p.amountRevenue) || Number(p.amount) || 0), 0);

  // Team cost (split by COGS / OPEX / Marketing). Allocation base is the
  // total compensation paid that month: per-month salary override (falls
  // back to monthlySalary) plus any bonus recorded for that month.
  const contributingTeam = { cogs: [], opex: [], marketing: [] };
  let teamCogs = 0;
  let teamOpex = 0;
  let teamMarketing = 0;
  for (const m of team) {
    if (!teamActiveInMonth(m, mKey)) continue;
    const base = resolveTeamMonthSalary(m, mKey) + resolveTeamMonthBonus(m, mKey);
    const c = costFromAllocations(m.offerAllocations, view, base, offers);
    teamCogs += c.cogs;
    teamOpex += c.opex;
    teamMarketing += c.marketing;
    if (c.cogs > 0) contributingTeam.cogs.push({ id: m.id, name: m.name, role: m.role, cost: c.cogs });
    if (c.opex > 0) contributingTeam.opex.push({ id: m.id, name: m.name, role: m.role, cost: c.opex });
    if (c.marketing > 0) contributingTeam.marketing.push({ id: m.id, name: m.name, role: m.role, cost: c.marketing });
  }

  // Tool cost (split by COGS / OPEX / Marketing). Base falls back from
  // planned (units × price) to actual matched bank-feed expenses when no
  // plan is set — so variable-cost tools (email inboxes, credit packs)
  // still feed P&L.
  const contributingTools = { cogs: [], opex: [], marketing: [] };
  let toolCogs = 0;
  let toolOpex = 0;
  let toolMarketing = 0;
  const oneOffsForTools = state.oneOffPayments || [];
  for (const t of tools) {
    const base = resolveToolMonthBase(t, mKey, oneOffsForTools);
    if (base <= 0) continue;
    const c = costFromAllocations(t.offerAllocations, view, base, offers);
    toolCogs += c.cogs;
    toolOpex += c.opex;
    toolMarketing += c.marketing;
    if (c.cogs > 0) contributingTools.cogs.push({ id: t.id, name: t.name, cost: c.cogs });
    if (c.opex > 0) contributingTools.opex.push({ id: t.id, name: t.name, cost: c.opex });
    if (c.marketing > 0) contributingTools.marketing.push({ id: t.id, name: t.name, cost: c.marketing });
  }

  // One-off payments (renamed "Transactions" in the UI) are bank-import
  // reconciliation rows. They are NOT counted toward P&L cost — the source
  // of truth for cost is the Tools tab (units × price per month, covering
  // both recurring and one-off tools) plus Team salaries.
  // Transactions matched to a Tool/Salary verify the planned cost; they
  // don't add to it (avoid double-counting). Flagged rows stay excluded.
  const oneOffsByType = { cogs: [], opex: [], marketing: [] };
  const oneOffCogs = 0;
  const oneOffOpex = 0;
  const oneOffMarketing = 0;

  const cogs = {
    team: teamCogs,
    tools: toolCogs,
    oneOffs: oneOffCogs,
    total: teamCogs + toolCogs + oneOffCogs,
  };
  const opex = {
    team: teamOpex,
    tools: toolOpex,
    oneOffs: oneOffOpex,
    total: teamOpex + toolOpex + oneOffOpex,
  };
  const marketing = {
    team: teamMarketing,
    tools: toolMarketing,
    oneOffs: oneOffMarketing,
    total: teamMarketing + toolMarketing + oneOffMarketing,
  };
  const grossProfit = revenue - cogs.total;
  const netProfit = grossProfit - opex.total - marketing.total;

  return {
    revenue, cogs, opex, marketing, grossProfit, netProfit,
    contributingTeam, contributingTools, oneOffsByType,
  };
}

// Monthly P&L time series for one offer over the last N months (or agency
// view if offerId is null).
export function getOfferPnlSeries(offerId, state, monthsBack = 6) {
  const t = today();
  const series = [];
  for (let i = -monthsBack + 1; i <= 0; i++) {
    const m = addMonths(t, i);
    const mKey = monthKeyFromDate(m);
    const monthData = aggregateOfferMonth(offerId, state, mKey);
    series.push({
      month: m.toLocaleDateString("en-US", { month: "short", year: "2-digit" }),
      monthKey: mKey,
      revenue: monthData.revenue,
      cogs: monthData.cogs.total,
      opex: monthData.opex.total,
      grossProfit: monthData.grossProfit,
      netProfit: monthData.netProfit,
    });
  }
  return series;
}

// Sum across all-time for an offer (or agency view if offerId is null).
// Walks every month that has any tool data + every paid payment +
// every one-off, and sums per-month aggregates into the lifetime P&L.
export function aggregateOfferAllTime(offerId, state) {
  const payments = state.payments || [];
  const oneOffs = state.oneOffPayments || [];
  const tools = state.tools || [];
  const clients = state.clients || [];

  // Collect every month key that could contribute to costs/revenue
  const allMonthKeys = new Set();
  tools.forEach((t) => {
    Object.keys(t.unitsByMonth || {}).forEach((k) => allMonthKeys.add(k));
    Object.keys(t.priceByMonth || {}).forEach((k) => allMonthKeys.add(k));
  });
  payments.forEach((p) => {
    if (p.status !== "paid") return;
    if (offerId != null && paymentOfferId(p, clients) !== offerId) return;
    const ref = p.paidDate || p.bookedDate;
    if (ref) allMonthKeys.add(monthKey(ref));
  });
  oneOffs.forEach((o) => {
    if (offerId != null && o.offerId !== offerId) return;
    if (o.date) allMonthKeys.add(monthKey(o.date));
  });

  let revenue = 0;
  const cogs = { team: 0, tools: 0, oneOffs: 0, total: 0 };
  const opex = { team: 0, tools: 0, oneOffs: 0, total: 0 };
  for (const mKey of allMonthKeys) {
    const m = aggregateOfferMonth(offerId, state, mKey);
    revenue += m.revenue;
    cogs.team += m.cogs.team;
    cogs.tools += m.cogs.tools;
    cogs.oneOffs += m.cogs.oneOffs;
    cogs.total += m.cogs.total;
    opex.team += m.opex.team;
    opex.tools += m.opex.tools;
    opex.oneOffs += m.opex.oneOffs;
    opex.total += m.opex.total;
  }
  const grossProfit = revenue - cogs.total;
  const netProfit = grossProfit - opex.total;
  return { revenue, cogs, opex, grossProfit, netProfit };
}

// Agency-wide P&L convenience wrappers — same as the offer aggregators
// but with offerId = null so they sum across every offer + every
// unattributed cost.
export function aggregateAgencyMonth(state, mKey) {
  return aggregateOfferMonth(null, state, mKey);
}
export function aggregateAgencyAllTime(state) {
  return aggregateOfferAllTime(null, state);
}
export function getAgencyPnlSeries(state, monthsBack = 6) {
  return getOfferPnlSeries(null, state, monthsBack);
}

// ── Department-level P&L ───────────────────────────────────────────────────
// A department (e.g. "SEO", "Cold Email") is a tag on offers. Its P&L is the
// union of all offers carrying that department tag, plus department-tagged
// one-off expenses (transactions with `department === <dept>`).
//
// Returns the same shape as aggregateOfferMonth plus:
//   - partnerCutsOwed : sum of all partner rules scoped to this dept this month
//   - clientBudgetCollected / clientBudgetSpent : pass-through ledger for this dept
//
// Pass dept = null to get an "Unassigned" bucket (offers/expenses with no department).
export function aggregateDepartmentMonth(departmentName, state, mKey) {
  const payments = state.payments || [];
  const clients = state.clients || [];
  const offers = state.offers || [];
  const team = state.team || [];
  const tools = state.tools || [];
  const oneOffs = state.oneOffPayments || [];
  const partnerRules = state.partnerRules || [];

  // The set of offer IDs that belong to this department. Offers with a null
  // department land in the "Unassigned" bucket when departmentName is null.
  const offerIdsInDept = new Set(
    offers
      .filter((o) => (departmentName == null ? !o.department : o.department === departmentName))
      .map((o) => o.id)
  );

  const inDept = (offerId) => offerId != null && offerIdsInDept.has(offerId);

  // ── Revenue ───────────────────────────────────────────────────────────
  // Uses amountRevenue (mapper falls back to amount for legacy rows).
  const revenue = payments
    .filter((p) => p.status === "paid")
    .filter((p) => inDept(paymentOfferId(p, clients)))
    .filter((p) => {
      const ref = p.paidDate || p.bookedDate;
      return ref && monthKey(ref) === mKey;
    })
    .reduce((s, p) => s + (Number(p.amountRevenue) || Number(p.amount) || 0), 0);

  // ── Team cost ─────────────────────────────────────────────────────────
  // Uses costFromAllocations with view={type:"department"} so it understands
  // all three scopes (offer / department / agency). Agency-scope allocations
  // are intentionally excluded from department margins — they live at agency-
  // total only.
  const deptView = { type: "department", department: departmentName };
  const contributingTeam = { cogs: [], opex: [], marketing: [] };
  let teamCogs = 0;
  let teamOpex = 0;
  let teamMarketing = 0;
  for (const m of team) {
    if (!teamActiveInMonth(m, mKey)) continue;
    const base = resolveTeamMonthSalary(m, mKey) + resolveTeamMonthBonus(m, mKey);
    const c = costFromAllocations(m.offerAllocations, deptView, base, offers);
    teamCogs += c.cogs;
    teamOpex += c.opex;
    teamMarketing += c.marketing;
    if (c.cogs > 0) contributingTeam.cogs.push({ id: m.id, name: m.name, role: m.role, cost: c.cogs });
    if (c.opex > 0) contributingTeam.opex.push({ id: m.id, name: m.name, role: m.role, cost: c.opex });
    if (c.marketing > 0) contributingTeam.marketing.push({ id: m.id, name: m.name, role: m.role, cost: c.marketing });
  }

  // ── Tool cost ─────────────────────────────────────────────────────────
  // Same planned-or-actual fallback as the offer aggregator.
  const contributingTools = { cogs: [], opex: [], marketing: [] };
  let toolCogs = 0;
  let toolOpex = 0;
  let toolMarketing = 0;
  for (const t of tools) {
    const base = resolveToolMonthBase(t, mKey, oneOffs);
    if (base <= 0) continue;
    const c = costFromAllocations(t.offerAllocations, deptView, base, offers);
    toolCogs += c.cogs;
    toolOpex += c.opex;
    toolMarketing += c.marketing;
    if (c.cogs > 0) contributingTools.cogs.push({ id: t.id, name: t.name, cost: c.cogs });
    if (c.opex > 0) contributingTools.opex.push({ id: t.id, name: t.name, cost: c.opex });
    if (c.marketing > 0) contributingTools.marketing.push({ id: t.id, name: t.name, cost: c.marketing });
  }

  // ── One-off / bank-feed expenses tagged to this department ───────────
  // Match transactions whose direct `department` field equals this dept AND
  // which are matched + counted (excluding pass-through `client_budget` rows
  // and the new "non-expense" match types). This is additive to team/tool
  // costs since these are bank-confirmed extras the planned cost didn't cover.
  const NON_EXPENSE_MATCH_TYPES = new Set([
    "owner_withdrawal", "bank_transfer", "revenue_dedup", "partner_payout",
  ]);
  const oneOffsByType = { cogs: [], opex: [] };
  let oneOffCogs = 0;
  let oneOffOpex = 0;
  for (const o of oneOffs) {
    if (!o.date || monthKey(o.date) !== mKey) continue;
    if (o.department !== departmentName) continue;
    if (o.status === "flagged") continue;
    if ((o.costType || "opex") === "client_budget") continue; // pass-through
    if (o.matchedSourceType && NON_EXPENSE_MATCH_TYPES.has(o.matchedSourceType)) continue;
    // Skip rows already attributed via tool/team allocations to avoid double
    // counting — those use planned cost (units × price + salary).
    if (o.matchedSourceType === "tool" || o.matchedSourceType === "salary") continue;
    const sign = (o.direction || "out") === "out" ? 1 : -1;
    const amt = sign * (Number(o.amount) || 0);
    if ((o.costType || "opex") === "cogs") {
      oneOffCogs += amt;
      oneOffsByType.cogs.push({ id: o.id, name: o.name, amount: amt });
    } else {
      oneOffOpex += amt;
      oneOffsByType.opex.push({ id: o.id, name: o.name, amount: amt });
    }
  }

  const cogs = {
    team: teamCogs, tools: toolCogs, oneOffs: oneOffCogs,
    total: teamCogs + toolCogs + oneOffCogs,
  };
  const opex = {
    team: teamOpex, tools: toolOpex, oneOffs: oneOffOpex,
    total: teamOpex + toolOpex + oneOffOpex,
  };
  const marketing = {
    team: teamMarketing, tools: toolMarketing, oneOffs: 0,
    total: teamMarketing + toolMarketing,
  };
  const grossProfit = revenue - cogs.total;
  const netProfit = grossProfit - opex.total - marketing.total;

  // ── Partner cuts owed (department-scoped rules only) ─────────────────
  // Rules with scope='offer' targeting an offer in this dept also count.
  // Agency-scoped rules are NOT included here — they appear in the agency view.
  let partnerCutsOwed = 0;
  for (const rule of partnerRules) {
    if (!isRuleEffectiveInMonth(rule, mKey)) continue;
    const matchesScope =
      (rule.scope === "department" && rule.scopeId === departmentName) ||
      (rule.scope === "offer" && rule.scopeId && offerIdsInDept.has(rule.scopeId));
    if (!matchesScope) continue;
    const base = rule.basis === "net_profit" ? netProfit : revenue;
    partnerCutsOwed += Math.max(0, base) * (Number(rule.percent) || 0) / 100;
  }

  return {
    revenue, cogs, opex, marketing, grossProfit, netProfit,
    contributingTeam, contributingTools, oneOffsByType,
    partnerCutsOwed,
  };
}

// 12-month department P&L series (most recent N months).
export function getDepartmentPnlSeries(departmentName, state, monthsBack = 6) {
  const t = today();
  const series = [];
  for (let i = -monthsBack + 1; i <= 0; i++) {
    const m = addMonths(t, i);
    const mKey = monthKeyFromDate(m);
    const monthData = aggregateDepartmentMonth(departmentName, state, mKey);
    series.push({
      month: m.toLocaleDateString("en-US", { month: "short", year: "2-digit" }),
      monthKey: mKey,
      revenue: monthData.revenue,
      cogs: monthData.cogs.total,
      opex: monthData.opex.total,
      grossProfit: monthData.grossProfit,
      netProfit: monthData.netProfit,
      partnerCutsOwed: monthData.partnerCutsOwed,
    });
  }
  return series;
}

// ── Partner cuts ───────────────────────────────────────────────────────────
// Is a rule effective for the given month (mKey "YYYY-MM")?
// Treats the rule's startsOn / endsOn as inclusive month bounds.
function isRuleEffectiveInMonth(rule, mKey) {
  const monthStart = `${mKey}-01`;
  const [y, m] = mKey.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const monthEnd = `${mKey}-${String(daysInMonth).padStart(2, "0")}`;
  if (rule.startsOn && rule.startsOn > monthEnd) return false;
  if (rule.endsOn && rule.endsOn < monthStart) return false;
  return true;
}

// What does a single partner owe / get paid in a given month?
// Sums every effective rule for the partner in that month and computes the
// cut against the rule's basis (revenue or net_profit) at the rule's scope
// (department / offer / agency).
//   returns: { owed, breakdown: [{ ruleId, basis, scope, scopeId, percent, base, amount }] }
export function computePartnerOwedForMonth(partnerId, mKey, state) {
  const partnerRules = state.partnerRules || [];
  const rules = partnerRules.filter((r) => r.partnerId === partnerId && isRuleEffectiveInMonth(r, mKey));
  const breakdown = [];
  let owed = 0;

  for (const rule of rules) {
    // Compute base for this rule's scope.
    let base = 0;
    if (rule.scope === "agency") {
      const agg = aggregateAgencyMonth(state, mKey);
      base = rule.basis === "net_profit" ? agg.netProfit : agg.revenue;
    } else if (rule.scope === "department") {
      const agg = aggregateDepartmentMonth(rule.scopeId, state, mKey);
      base = rule.basis === "net_profit" ? agg.netProfit : agg.revenue;
    } else if (rule.scope === "offer") {
      const agg = aggregateOfferMonth(rule.scopeId, state, mKey);
      base = rule.basis === "net_profit" ? agg.netProfit : agg.revenue;
    }
    const amount = Math.max(0, base) * (Number(rule.percent) || 0) / 100;
    owed += amount;
    breakdown.push({
      ruleId: rule.id, basis: rule.basis, scope: rule.scope, scopeId: rule.scopeId,
      percent: Number(rule.percent) || 0, base, amount,
    });
  }

  return { owed, breakdown };
}

// Sum partner cuts already PAID to a partner in a given month, sourced from
// bank-feed transactions tagged matchedSourceType='partner_payout' +
// matchedSourceId=<partnerId>. Refunds (direction='in') subtract.
export function computePartnerPaidForMonth(partnerId, mKey, state) {
  const oneOffs = state.oneOffPayments || [];
  return oneOffs
    .filter((o) => o.matchedSourceType === "partner_payout" && o.matchedSourceId === partnerId)
    .filter((o) => o.date && monthKey(o.date) === mKey)
    .reduce((s, o) => {
      const sign = (o.direction || "out") === "out" ? 1 : -1;
      return s + sign * (Number(o.amount) || 0);
    }, 0);
}

// ── Client budget ledger (pass-through model) ──────────────────────────────
// For SEO-style deals where a $10k payment is split into $3k commission +
// $7k client budget that we hold to spend on backlinks for them.
//
// opts: { clientId?, offerId?, throughMonth? }
//   clientId  - filter both sides to one client
//   offerId   - filter both sides to one offer
//   throughMonth (YYYY-MM) - only count rows on/before this month (defaults to all)
//
// Returns: { collected, spent, held }
//   collected = total client-budget portion of paid payments
//   spent     = total expenses where costType='client_budget'
//   held      = collected - spent (the running liability)
export function computeClientBudgetHeld(state, opts = {}) {
  const payments = state.payments || [];
  const oneOffs = state.oneOffPayments || [];
  const { clientId, offerId, throughMonth } = opts;

  const monthLte = (dateStr) => {
    if (!throughMonth || !dateStr) return true;
    return monthKey(dateStr) <= throughMonth;
  };

  const collected = payments
    .filter((p) => p.status === "paid")
    .filter((p) => !clientId || p.clientId === clientId)
    .filter((p) => !offerId || p.offerId === offerId)
    .filter((p) => monthLte(p.paidDate || p.bookedDate))
    .reduce((s, p) => s + (Number(p.amountClientBudget) || 0), 0);

  const spent = oneOffs
    .filter((o) => (o.costType || "opex") === "client_budget")
    .filter((o) => !offerId || o.offerId === offerId)
    // clientId filter intentionally skipped on the expense side — one_off_payments
    // doesn't carry client_id yet (Phase 3 migration). Aggregate totals will be
    // correct; per-client drill-down will be enabled once the column exists.
    .filter((o) => monthLte(o.date))
    .reduce((s, o) => {
      const sign = (o.direction || "out") === "out" ? 1 : -1;
      return s + sign * (Number(o.amount) || 0);
    }, 0);

  return { collected, spent, held: collected - spent };
}

// ── Agency-by-department breakdown ─────────────────────────────────────────
// Convenience wrapper: agency-level P&L for a month plus a per-department
// breakdown using the configured departments list. Anything in an offer
// without a department lands in the "Unassigned" key.
export function aggregateAgencyMonthByDepartment(state, mKey) {
  const agency = aggregateAgencyMonth(state, mKey);
  const departments = state.settings?.departments || [];
  const byDepartment = {};
  for (const dept of departments) {
    byDepartment[dept] = aggregateDepartmentMonth(dept, state, mKey);
  }
  // Catch-all bucket for offers with a null department.
  byDepartment.__unassigned__ = aggregateDepartmentMonth(null, state, mKey);

  // Agency-scoped partner cuts (rules with scope='agency')
  const partnerRules = state.partnerRules || [];
  let agencyPartnerCutsOwed = 0;
  for (const rule of partnerRules) {
    if (rule.scope !== "agency") continue;
    if (!isRuleEffectiveInMonth(rule, mKey)) continue;
    const base = rule.basis === "net_profit" ? agency.netProfit : agency.revenue;
    agencyPartnerCutsOwed += Math.max(0, base) * (Number(rule.percent) || 0) / 100;
  }

  return { ...agency, byDepartment, agencyPartnerCutsOwed };
}

// ── Per-department cash & revenue helpers ──────────────────────────────────
// These power the new "accounting-software" view: Revenue (expected) vs
// Cash Collected (real) per department, plus virtual Cash in Bank per dept.

// Resolve which department a payment rolls up to.
// payment → offer (via paymentOfferId, which falls back to client.primaryOfferId)
// → offer.department.
function paymentDepartment(payment, offers, clients) {
  const offerId = paymentOfferId(payment, clients);
  if (!offerId) return null;
  const offer = offers.find((o) => o.id === offerId);
  return offer?.department || null;
}

// Cash actually collected for a department in [fromDate, toDate] (ISO strings).
// Counts only payments with status="paid" and a paidDate inside the window.
// Uses amountRevenue when present (so client-budget portions don't double-count
// as agency revenue) and falls back to amount otherwise.
export function computeDepartmentCashCollected(dept, state, fromDate, toDate) {
  const payments = state.payments || [];
  const clients = state.clients || [];
  const offers = state.offers || [];
  return payments
    .filter((p) => p.status === "paid")
    .filter((p) => paymentDepartment(p, offers, clients) === dept)
    .filter((p) => {
      const d = p.paidDate || p.bookedDate;
      if (!d) return false;
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      return true;
    })
    .reduce((s, p) => s + (Number(p.amountRevenue ?? p.amount) || 0), 0);
}

// MRR (recurring) per department — sum of active recurring clients in the dept
// at the start of the given month, multiplied by their monthlyRate. Mirrors
// calculateMRR but scoped to one department.
export function computeDepartmentMRR(dept, state, mKey) {
  const clients = state.clients || [];
  const offers = state.offers || [];
  const recurringOfferIdsForDept = new Set(
    offers.filter((o) => o.type === "recurring" && (o.department || null) === dept).map((o) => o.id)
  );
  const monthStart = `${mKey}-01`;
  let mrr = 0;
  for (const c of clients) {
    if (c.status !== "active") continue;
    if (!recurringOfferIdsForDept.has(c.primaryOfferId)) continue;
    if (c.startDate && c.startDate > monthStart) continue;
    if (c.churnDate && c.churnDate <= monthStart) continue;
    mrr += Number(c.monthlyRate) || 0;
  }
  return mrr;
}

// Expected revenue per dept for a month = paid + pending + scheduled
// payments whose due/booked date lands in that month.
export function computeDepartmentRevenue(dept, state, mKey) {
  const payments = state.payments || [];
  const clients = state.clients || [];
  const offers = state.offers || [];
  return payments
    .filter((p) => ["paid", "pending", "scheduled"].includes(p.status))
    .filter((p) => paymentDepartment(p, offers, clients) === dept)
    .filter((p) => {
      const d = p.paidDate || p.dueDate || p.bookedDate;
      return d && d.slice(0, 7) === mKey;
    })
    .reduce((s, p) => s + (Number(p.amountRevenue ?? p.amount) || 0), 0);
}

// "New sales" — cash collected this month MINUS the portion that's just
// recurring (existing MRR clients paying their monthly). Captures upsells,
// new clients, and one-off deals.
export function computeDepartmentNewSales(dept, state, mKey) {
  const monthStart = `${mKey}-01`;
  const monthEnd = `${mKey}-31`;
  const cashCollected = computeDepartmentCashCollected(dept, state, monthStart, monthEnd);
  const mrr = computeDepartmentMRR(dept, state, mKey);
  return Math.max(0, cashCollected - mrr);
}

// Sum of dividends taken from this department in [fromDate, toDate].
export function computeDepartmentDividends(dept, state, fromDate, toDate) {
  const dividends = state.dividends || [];
  return dividends
    .filter((d) => (d.department || null) === dept)
    .filter((d) => {
      if (!d.date) return false;
      if (fromDate && d.date < fromDate) return false;
      if (toDate && d.date > toDate) return false;
      return true;
    })
    .reduce((s, d) => s + (Number(d.amount) || 0), 0);
}

// Expenses paid per dept in [fromDate, toDate] — only counts one_off rows
// (the QB feed) that are tagged to this dept either directly via
// `oneOff.department` or indirectly via a matched tool/team member whose
// department matches. Refunds (direction "in") subtract.
export function computeDepartmentExpensesPaid(dept, state, fromDate, toDate) {
  const oneOffs = state.oneOffPayments || [];
  const tools = state.tools || [];
  const team = state.team || [];
  return oneOffs
    .filter((p) => {
      const direct = p.department;
      if (direct) return direct === dept;
      // Fall back to matched source's department
      if (p.matchedSourceType === "tool") {
        const tool = tools.find((t) => t.id === p.matchedSourceId);
        // Tools don't carry a direct department — they distribute via
        // offerAllocations. For simplicity here, only count tool rows that
        // resolve through offers; otherwise leave unassigned.
        return false;
      }
      if (p.matchedSourceType === "salary") {
        const member = team.find((m) => m.id === p.matchedSourceId);
        return (member?.department || null) === dept;
      }
      return false;
    })
    .filter((p) => {
      if (!p.date) return false;
      if (fromDate && p.date < fromDate) return false;
      if (toDate && p.date > toDate) return false;
      return true;
    })
    .reduce((s, p) => {
      const sign = (p.direction || "out") === "out" ? 1 : -1;
      return s + sign * (Number(p.amount) || 0);
    }, 0);
}

// Stripe processor fees for a department in [fromDate, toDate].
// Stripe fees are deducted from gross revenue before it hits the bank, so
// they reduce Net Revenue and Cash in Bank. Only payments with
// _source==="stripe" carry a fee — manual / GHL rows have none.
export function computeDepartmentStripeFees(dept, state, fromDate, toDate) {
  const payments = state.payments || [];
  const clients = state.clients || [];
  const offers = state.offers || [];
  return payments
    .filter((p) => p._source === "stripe" && p.status === "paid")
    .filter((p) => paymentDepartment(p, offers, clients) === dept)
    .filter((p) => {
      const d = p.paidDate || p.bookedDate;
      if (!d) return false;
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      return true;
    })
    .reduce((s, p) => s + (Number(p.stripeFee) || 0), 0);
}

// Virtual cash in bank for a department, as-of date inclusive.
// = lifetime cash collected − Stripe fees − lifetime expenses paid − lifetime dividends.
// Fees never reach the bank so they belong on the "out" side too.
export function computeDepartmentCashInBank(dept, state, asOf) {
  const cashCollected = computeDepartmentCashCollected(dept, state, null, asOf);
  const stripeFees = computeDepartmentStripeFees(dept, state, null, asOf);
  const expensesPaid = computeDepartmentExpensesPaid(dept, state, null, asOf);
  const dividendsPaid = computeDepartmentDividends(dept, state, null, asOf);
  return cashCollected - stripeFees - expensesPaid - dividendsPaid;
}

// Per-dept client budget held: aggregate the existing per-client
// computeClientBudgetHeld for every client in this department.
export function computeDepartmentBudgetHeld(dept, state, throughMonth) {
  const clients = state.clients || [];
  const offers = state.offers || [];
  let collected = 0;
  let spent = 0;
  // 1. Per-client portion: filter to clients in this department.
  for (const c of clients) {
    const offer = offers.find((o) => o.id === c.primaryOfferId);
    if ((offer?.department || null) !== dept) continue;
    const r = computeClientBudgetHeld(state, { clientId: c.id, throughMonth });
    collected += r.collected;
    // Note: spent computation in computeClientBudgetHeld is GLOBAL
    // (one_off_payments doesn't carry client_id yet) so we don't add spent here
    // to avoid over-counting. The "spent" total is approximated below.
  }
  // 2. Spent from one-offs tagged to this department with costType="client_budget"
  const oneOffs = state.oneOffPayments || [];
  for (const o of oneOffs) {
    if ((o.costType || "opex") !== "client_budget") continue;
    if ((o.department || null) !== dept) continue;
    if (throughMonth && monthKey(o.date) > throughMonth) continue;
    const sign = (o.direction || "out") === "out" ? 1 : -1;
    spent += sign * (Number(o.amount) || 0);
  }
  return { collected, spent, held: collected - spent };
}

// Next N upcoming invoices for a dept — sorted by dueDate ascending,
// pulling from pending + scheduled payments only.
export function computeNextInvoicesDue(dept, state, limit = 5, asOf = null) {
  const payments = state.payments || [];
  const clients = state.clients || [];
  const offers = state.offers || [];
  const today = asOf || new Date().toISOString().slice(0, 10);
  return payments
    .filter((p) => p.status === "scheduled" || p.status === "pending")
    .filter((p) => paymentDepartment(p, offers, clients) === dept)
    .filter((p) => p.dueDate && p.dueDate >= today)
    .sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || ""))
    .slice(0, limit)
    .map((p) => {
      const client = clients.find((c) => c.id === p.clientId);
      const offer = offers.find((o) => o.id === p.offerId) || offers.find((o) => o.id === client?.primaryOfferId);
      return {
        id: p.id,
        clientName: client?.name || "—",
        offerName: offer?.name || "—",
        amount: Number(p.amount) || 0,
        dueDate: p.dueDate,
        note: p.note || "",
      };
    });
}

// Agency cash in bank = sum of every department's cash in bank + unassigned.
export function computeAgencyCashInBank(state, asOf) {
  const departments = state.settings?.departments || [];
  let total = 0;
  for (const dept of departments) total += computeDepartmentCashInBank(dept, state, asOf);
  total += computeDepartmentCashInBank(null, state, asOf);
  return total;
}
